import { forwardRef } from 'react';
import { ArrowLeft, Printer } from '@phosphor-icons/react';
import { useLanguage } from '../context/LanguageContext';

/** Label keys (t) and DB column prefixes must stay aligned. */
const PRINT_BESTELLING_ROWS = [
  { labelKey: 'kozijnen', dbKey: 'kozijnen' },
  { labelKey: 'glas', dbKey: 'glas' },
  { labelKey: 'deurvulling', dbKey: 'deurvulling' },
  { labelKey: 'rooster', dbKey: 'rooster' },
  { labelKey: 'horren', dbKey: 'horren' },
  { labelKey: 'keralit', dbKey: 'keralit' },
  { labelKey: 'binnenafwerking', dbKey: 'binnenafwerking' },
  { labelKey: 'vensterbanken', dbKey: 'vensterbanken' },
  { labelKey: 'dakramenBmn', dbKey: 'dakramen_bmn' },
  { labelKey: 'plastisol', dbKey: 'plastisol' },
  { labelKey: 'raamdorpelstenen', dbKey: 'raamdorpelstenen' },
  { labelKey: 'stelpakket', dbKey: 'stelpakket' },
  { labelKey: 'rolluikenScreens', dbKey: 'rolluiken_screens' },
  { labelKey: 'sandwich', dbKey: 'sandwich' },
  { labelKey: 'container', dbKey: 'container' },
  { labelKey: 'diversen', dbKey: 'diversen' },
];

function bestellingRowHasContent(order, dbKey) {
  if (dbKey === 'diversen') {
    return (
      String(order.diversen_besteldatum ?? '').trim() ||
      String(order.diversen_leverdatum ?? '').trim()
    );
  }
  return (
    String(order[`${dbKey}_besteldatum`] ?? '').trim() ||
    String(order[`${dbKey}_leverdatum`] ?? '').trim()
  );
}

export const printLogoSrc = () =>
  typeof window !== 'undefined' && window.location?.origin
    ? `${window.location.origin}/vmk-logo.png`
    : '/vmk-logo.png';

const S = {
  box: {
    border: '1.5px solid #222',
    padding: '4px 10px',
    fontSize: '12.5px',
    fontWeight: 600,
    color: '#222',
    whiteSpace: 'nowrap',
    lineHeight: '21px',
    textAlign: 'center',
    verticalAlign: 'bottom',
  },
  val: {
    borderBottom: '1.5px solid #222',
    fontSize: '12.5px',
    color: '#222',
    lineHeight: '21px',
    padding: '4px 6px',
    textAlign: 'center',
    verticalAlign: 'bottom',
    maxWidth: '0',
  },
  empty: {
    borderBottom: '1.5px solid #222',
    lineHeight: '21px',
    padding: '4px 6px',
    verticalAlign: 'bottom',
    maxWidth: '0',
  },
};

export function VoorbladDocument({ order, contentRef = null }) {
  const { t, language } = useLanguage();
  if (!order) return null;
  const fd = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString(language === 'en' ? 'en-GB' : 'nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const company = order.company_name || 'THERMO-SOLUTIONS MONTEURS';
  const monteurs = order.monteurs || '';

  const clientRowDefs = [
    [t('clientLabel'), order.client_name],
    [t('addressLabel'), order.address],
    [t('postcodeCityLabel'), order.postcode],
    [t('phoneLabel'), order.phone],
    [t('emailLabel'), order.email],
  ];
  const clientRowsFiltered = clientRowDefs.filter(([, v]) => String(v ?? '').trim());
  const displayClientRows =
    clientRowsFiltered.length > 0
      ? clientRowsFiltered
      : [[t('clientLabel'), order.client_name || '—']];

  const hasMonteurs = String(monteurs || '').trim();
  const hasCompanyOrInstallers = String(company || '').trim() || hasMonteurs;

  const hasDatesBlock =
    ['verkoopdatum', 'inmeetdatum', 'montagedatum'].some((k) => String(order[k] ?? '').trim()) ||
    Number(order.montagedagen) > 0;

  const bestellingRowsToPrint = PRINT_BESTELLING_ROWS.filter(({ dbKey }) =>
    bestellingRowHasContent(order, dbKey),
  );

  return (
    <div
      ref={contentRef}
      className="print-content"
      style={{
        padding: '12px 20px 12px 20px',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      }}
    >
          {/* Logo + title (print) */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, auto) 1fr minmax(0, auto)',
              alignItems: 'center',
              columnGap: '12px',
              marginBottom: '12px',
            }}
          >
            <img
              src={printLogoSrc()}
              alt="VMK Kozijnen"
              style={{ height: '50px', width: 'auto', maxWidth: '225px', objectFit: 'contain', display: 'block' }}
            />
            <div style={{ textAlign: 'center' }}>
              <span style={{ fontSize: '17px', fontWeight: 600, color: '#222' }}>{t('coverSheetTitle')}</span>
            </div>
            <div style={{ width: '48px' }} aria-hidden="true" />
          </div>

          {/* CLIENT + COMPANY — only non-empty client fields; sidebar shows company when relevant */}
          <table style={{ width: '90%', borderCollapse: 'separate', borderSpacing: '0 5px', marginBottom: '12px' }}>
            <tbody>
              {displayClientRows.map(([label, value], i) => (
                <tr key={label}>
                  <td style={{ ...S.box, width: '155px', textAlign: 'left' }}>{label}</td>
                  <td style={S.val}>{value}</td>
                  {i === 0 && hasCompanyOrInstallers && (
                    <td rowSpan={displayClientRows.length} style={{ paddingLeft: '20px', verticalAlign: 'top', width: '220px' }}>
                      {String(company || '').trim() ? (
                        <div style={{ fontSize: '14px', fontWeight: 600, color: '#c0392b', marginBottom: hasMonteurs ? '8px' : 0, textAlign: 'center' }}>{company}</div>
                      ) : null}
                      {hasMonteurs ? (
                        <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0 5px' }}>
                          <tbody>
                            <tr>
                              <td style={{ ...S.box, textAlign: 'left', padding: '3px 4px', fontSize: '11px', width: '55px' }}>{t('installersLabel')}</td>
                              <td style={S.val}>{monteurs}</td>
                            </tr>
                          </tbody>
                        </table>
                      ) : null}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>

          {/* Color */}
          <table style={{ width: '90%', borderCollapse: 'separate', borderSpacing: '0 5px', marginBottom: '12px' }}>
            <tbody>
              {[
                [t('exteriorFrameLabel'), order.kozijn_buiten, order.folie_kozijn_buiten],
                [t('exteriorSashLabel'), order.draaideel_buiten, order.folie_draaideel_buiten],
                [t('interiorFrameLabel'), order.kozijn_binnen, order.folie_kozijn_binnen],
              ].map(([label, code, folie]) => (
                <tr key={label}>
                  <td style={{ ...S.box, width: '165px', textAlign: 'left' }}>{label}</td>
                  <td style={S.val}>{code || ''}</td>
                  <td style={{ width: '14px' }}></td>
                  <td style={{ ...S.box, width: '80px' }}>{t('foilLabel')}</td>
                  <td style={S.val}>{folie ? t('yesLabel') : t('noLabel')}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* DATES — only when at least one value is set */}
          {hasDatesBlock && (
          <table style={{ width: '90%', borderCollapse: 'separate', borderSpacing: '0 5px', marginBottom: '12px' }}>
            <tbody>
              <tr>
                <td style={{ ...S.box, width: '130px', textAlign: 'left' }}>{t('salesDateLabel')}</td>
                <td style={S.val}>{fd(order.verkoopdatum)}</td>
                <td style={{ width: '14px' }}></td>
                <td style={{ ...S.box, width: '130px', textAlign: 'left' }}>{t('installDateLabel')}</td>
                <td style={S.val}>{fd(order.montagedatum)}</td>
              </tr>
              <tr>
                <td style={{ ...S.box, width: '130px', textAlign: 'left' }}>{t('measureDateLabel')}</td>
                <td style={S.val}>{fd(order.inmeetdatum)}</td>
                <td style={{ width: '14px' }}></td>
                <td style={{ ...S.box, width: '130px', textAlign: 'left' }}>{t('installDaysLabel')}</td>
                <td style={S.val}>{order.montagedagen || ''}</td>
              </tr>
            </tbody>
          </table>
          )}

          {/* BESTELLINGEN — only rows with content */}
          {bestellingRowsToPrint.length > 0 && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <table style={{ width: '90%', borderCollapse: 'separate', borderSpacing: '0 5px' }}>
              <thead>
                <tr>
                  <th style={{ ...S.box, width: '155px', textAlign: 'left' }}>{t('ordersHeaderLabel')}</th>
                  <th style={{ width: '10px' }}></th>
                  <th style={{ ...S.empty, fontSize: '12.5px', fontWeight: 600, color: '#222', textAlign: 'center' }}>{t('orderedDateHeader')}</th>
                  <th style={{ width: '14px' }}></th>
                  <th style={{ ...S.empty, fontSize: '12.5px', fontWeight: 600, color: '#222', textAlign: 'center' }}>{t('deliveryDateHeader')}</th>
                </tr>
              </thead>
              <tbody>
                {bestellingRowsToPrint.map(({ labelKey, dbKey }) => (
                  <tr key={dbKey}>
                    <td style={{ ...S.box, width: '155px', textAlign: 'left' }}>{t(labelKey)}</td>
                    <td style={{ width: '10px' }}></td>
                    <td style={S.val}>{order[`${dbKey}_besteldatum`] || ''}</td>
                    <td style={{ width: '14px' }}></td>
                    <td style={S.val}>{dbKey === 'diversen' ? '' : (order[`${dbKey}_leverdatum`] || '')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}

          {/* NOTES */}
          {order.notes && (
            <div style={{ marginTop: '8px', marginBottom: '6px' }}>
              <div style={{ borderBottom: '1.5px solid #222', padding: '4px 2px' }}>
                <span style={{ fontSize: '12.5px', color: '#c0392b', fontWeight: 600, whiteSpace: 'pre-wrap' }}>{order.notes}</span>
              </div>
            </div>
          )}

    </div>
  );
}

const OrderPrintView = forwardRef(({
  order,
  onClose,
  onPrint,
  headerActions = null,
  centerActions = null,
  hideDocument = false,
  showDefaultPrintButton = true,
}, ref) => {
  const { t } = useLanguage();
  if (!order) return null;

  return (
    <div className="animate-fadeIn" data-testid="order-print-view">
      <div className="flex flex-wrap items-center gap-3 mb-6 no-print">
        <button onClick={onClose} className="vmk-btn flex items-center gap-2" data-testid="back-to-orders-btn">
          <ArrowLeft size={20} weight="bold" />
          {t('backToOrders')}
        </button>
        <div className="flex-1 flex items-center justify-center">
          {centerActions}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {headerActions}
          {showDefaultPrintButton && (
            <button onClick={onPrint} className="vmk-btn vmk-btn-primary px-6 py-3 text-base font-semibold" data-testid="print-order-btn">
              <Printer size={20} weight="bold" />
              {t('printWerkvoorbereiding')}
            </button>
          )}
        </div>
      </div>

      {!hideDocument && (
        <div className="bg-white rounded-lg shadow-lg border border-[#e2e8f0] mx-auto" style={{ maxWidth: '210mm' }}>
          <VoorbladDocument order={order} contentRef={ref} />
        </div>
      )}
    </div>
  );
});

OrderPrintView.displayName = 'OrderPrintView';

export default OrderPrintView;
